<!--
Paginated View-datatable using vuetify datatable

Has header set, as well as search bar and translations.
Additionally, has different buttons that can be set to receive events for a specific row.
Buttons can also be shown depending on a true/false value given in the row.

Has handling for loading, sorting and searching using REST-api instead of only setting table data once.
-->
<template>
  <div>
    <v-card class="general-datatable ma-1" elevation="0">
      <v-card-title class="pa-5 pb-4">
        <p>{{ title }}</p>


        <v-btn v-if="addBtn" outlined color="primary" route :to="addBtnRoute" @click="clickedAddButton">
          <v-icon>add</v-icon>
          <span class="d-none d-sm-block">{{ addBtnText }}</span>
        </v-btn>

        <v-btn v-if="headButton" class="ml-1" outlined color="primary" @click="clickedHeadButton">
          <v-icon class="mr-2">{{headButtonIcon}}</v-icon>
          <span class="d-none d-sm-block">{{ headBtnText }}</span>
        </v-btn>

        <div v-if="customPanel" >
          <div ref="customPanel"/>
        </div>

        <v-btn v-show="filterName"
               class="ml-1"
               outlined color="primary"
               route :to="addBtnRoute"
               @click="() => {isFilterOpen = !isFilterOpen; $emit('filterStatusChanged', isFilterOpen) }">
          <v-icon>filter_alt</v-icon>
        </v-btn>

        <div class="text-center ml-1" v-show="headerSet && headerSet.length > 0">
          <v-menu
            v-model="menu"
            :close-on-content-click="false"
            :nudge-width="200"
            :max-height="'300px'"
            offset-x
          >
            <template v-slot:[`activator`]="{ on, attrs }">
              <v-btn
                outlined
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>view_column</v-icon>
              </v-btn>
            </template>
            <v-card >
              <v-container fluid >
                <v-checkbox
                  v-for="(header, index) in computedHeadersSet"
                  multiple
                  :hide-details="index !== header.length - 1"
                  v-model="selectedCheckBox"
                  :key="header.value"
                  :value="header.value"
                  :label="header.text"/>
              </v-container>
            </v-card>
          </v-menu>
        </div>

        <v-spacer></v-spacer>
        <v-text-field
          v-if="!disableSearch"
          v-model="search"
          append-icon="search"
          :value="computedSearch"
          :label="$t('components:common.search')"
          single-line
          hide-details
          clearable
          :disabled="isFilterOpen"
        />

        <TableFilter
          :key="filterKey"
          v-show="isFilterOpen"
          :headers="headers"
          :headerSet="headerSet"
          :filterName="filterName"
          @changeFilter="changeFilter"
        />

      </v-card-title>
      <v-card-text class="pa-0">
        <v-data-table
          ref="dataTable"
          :headers="selectedHeaders"
          :items="data"
          :search="search"
          :loading-text="$t('components:common.loading')"
          :no-data-text="$t('common:no-data-found')"
          :no-results-text="$t('components:datatable.noResults')"
          :footer-props="{itemsPerPageText: $t('components:datatable.itemsPerPage') ,
                          disableItemsPerPage: disableItemsPerPage,
                          itemsPerPageAllText: $t('common:all') }"
          :options.sync="options"
          :server-items-length="totalData"
          :loading="loading"
          :show-expand="expand"
          :show-select="showSelect"
          :items-per-page="itemsPerPage ? itemsPerPage : 10"
          v-model="selectedItems"
          @toggle-select-all="toggleAll"
          @pagination="clearSelection"
          :hide-default-footer="hideDefaultFooter"
          @update:options="clearSelection"
          @click:row="v => $emit('clickRow', v)"
        >

          <!-- Column as link -->
          <template v-if="linkRoute" v-slot:[`item.link`]="{ item }">
            <router-link :to="linkRoute + item[linkId]" class="font-weight-bold text-decoration-none">
              {{ item[linkName] }}
            </router-link>
          </template>

          <template v-if="linkRoute2" v-slot:[`item.link2`]="{ item }">
            <router-link :to="linkRoute2 + item[linkId2]" class="font-weight-bold text-decoration-none">
              {{ item[linkName2] }}
            </router-link>
          </template>

          <template v-if="linkRoute3" v-slot:[`item.link3`]="{ item }">
            <router-link :to="linkRoute3 + item[linkId3]" class="font-weight-bold text-decoration-none">
              {{ item[linkName3] }}
            </router-link>
          </template>

          <!-- Column as link with multiple IDs -->
          <template v-if="linkNameMultipleIDs" v-slot:[`item.linkMultipleIDs`]="{ item }">
            <router-link
              :to="linkRouteMultipleIDs1 + item[linkIdMultipleIDs1] + linkRouteMultipleIDs2 + item[linkIdMultipleIDs2]"
              class="font-weight-bold text-decoration-none">
              {{ item[linkNameMultipleIDs] }}
            </router-link>
          </template>

          <!-- Have to specifically set footer page text info for translation -->
          <template #[`footer.page-text`]="props">
            {{ props.pageStart }}-{{ props.pageStop }} {{ $t("components:datatable.page") }} {{ props.itemsLength }}
          </template>

          <!-- Additional image column -->
          <template v-slot:[`item.image`]="{ item }">
            <!-- check if the item has an image path and set it, otherwise set the alternative image value -->
            <v-img v-if="item != null && Object.prototype.hasOwnProperty.call(item, image) && item[image]"
                   max-height="64px"
                   max-width="64px"
                   :src="item[image]"/>
            <v-img v-else
                   max-height="64px"
                   max-width="64px"
                   :src="require('@/assets/' + imageAlt)"/>
          </template>

          <!-- Additional checkboxes that can be set to specific properties -->
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <template v-slot:[`item.checkbox2`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty2, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <template v-slot:[`item.checkbox3`]="{ item }">
            <v-icon>
              {{ checkShowCheckbox(checkboxOnProperty3, item) ? "check_box" : "check_box_outline_blank" }}
            </v-icon>
          </template>

          <!-- Additional buttons that can emit functions -->
          <template v-slot:[`item.controls`]="{ item }">
            <v-tooltip v-if="checkShowButton(icon, dependsOnProperty, item)" bottom :disabled="!iconTooltip">
              <template v-slot:activator="{ on, attrs}">
                <v-btn class="ma-2" icon :color="iconColor ? iconColor : 'primary'"
                       @click="clickedButton1(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ iconTooltip }}</span>
            </v-tooltip>

            <!-- Event for the second button -->
            <v-tooltip v-if="checkShowButton(icon2, dependsOnProperty2, item)" bottom :disabled="!iconTooltip2">
              <template v-slot:activator="{ on, attrs}">
                <v-btn class="ma-2" icon :color="iconColor2 ? iconColor2 : 'primary'"
                       @click="clickedButton2(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ icon2 }}</v-icon>
                </v-btn>
              </template>
              <span>{{ iconTooltip2 }}</span>
            </v-tooltip>

            <!-- Event for the third button -->
            <v-tooltip v-if="checkShowButton(icon3, dependsOnProperty3, item)" bottom :disabled="!iconTooltip3">
              <template v-slot:[`activator`]="{ on, attrs}">
                <v-btn class="ma-2" icon :color="iconColor3 ? iconColor3 : 'primary'"
                       @click="clickedButton3(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ icon3 }}</v-icon>
                </v-btn>
              </template>
              <span>{{ iconTooltip3 }}</span>
            </v-tooltip>

            <!-- Event for the fourth button -->
            <v-tooltip v-if="checkShowButton(icon4, dependsOnProperty4, item)" bottom :disabled="!iconTooltip4">
              <template v-slot:[`activator`]="{ on, attrs}">
                <v-btn class="ma-2" icon :color="iconColor4 ? iconColor4 : 'primary'"
                       @click="clickedButton4(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ icon4 }}</v-icon>
                </v-btn>
              </template>
              <span>{{ iconTooltip4 }}</span>
            </v-tooltip>

            <v-tooltip v-if="checkShowButton(icon5, dependsOnProperty4, item)" bottom :disabled="!iconTooltip5">
              <template v-slot:[`activator`]="{ on, attrs}">
                <v-btn class="ma-2" icon :color="iconColor5 ? iconColor5 : 'primary'"
                       @click="clickedButton5(item)" v-bind="attrs" v-on="on">
                  <v-icon>{{ icon5 }}</v-icon>
                </v-btn>
              </template>
              <span>{{ iconTooltip5 }}</span>
            </v-tooltip>
          </template>

          <template v-for="field in trueFalseDecorateFields" v-slot:[`item.${field}`]="{ item }" >
            <v-icon :key="field">
              {{returnLabel(item, field)}}
            </v-icon>
          </template>

          <template v-for="decorateField in decorateFields" v-slot:[`item.${decorateField.name}`]="{ item }" >
            <DecoratedField :key="decorateField.name" :item="item" :field="decorateField" />
          </template>

          <template v-if="expand" v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <ExpandedItemWrapper :item="item" :expandFn="expandFn" />
            </td>
          </template>

          <!-- Badges -->
          <template v-slot:[`item.badge`]="{ item }">
            <v-chip :color="item.badgeColor" small>
              <span class="font-weight-bold">
                {{ item.status }}
              </span>
            </v-chip>
          </template>

          <!-- Warning or normal text -->
          <template v-slot:[`item.warningOrNormal`]="{ item }">
            <span v-if="item.warning" class="scaleRed--text font-weight-bold">{{ item[warningOrNormalText] }}</span>
            <span v-else>{{ item[warningOrNormalText] }}</span>
          </template>
        </v-data-table>
        <div>
          <base-button v-if="showSelect"
                 :color="selectionButton1Color"
                 :disabled="selectedItems.length === 0"
                 @clickedButton="clickedSelectionButton"
                 :btn-text="selectionButton1Text"
          >
          </base-button>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>

import DecoratedField from './DataTablePaginated/DecoratedField';
import ExpandedItemWrapper from './DataTablePaginated/ExpandedItemWrapper';
import TableFilter from "./DataTablePaginated/TableFilter";
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: "DataTablePaginated",
  components: {
    DecoratedField,
    ExpandedItemWrapper,
    TableFilter,
    BaseButton
  },
  props: {
    title: String, //title of the datatable
    cachedSearch: String, // cached Search String from prev Search
    addBtn: Boolean, // optional Button for adding object
    headButton: Boolean, // optional Button
    addBtnRoute: String, // route for optional add Button
    addBtnText: String, // text of optional add Button
    headBtnText: String, // text of optional Button
    headers: Array, //header information
    headButtonIcon: String, //icon for optional Button
    headerSet: Array,
    hideDefaultFooter: Boolean,
    icon: String, //icon for the first button, if not given, button is not shown
    icon2: String, //^ second button
    icon3: String, //^ third button
    icon4: String, //^ fourth button
    icon5: String, //^ fifth button
    iconColor: String,
    iconColor2: String,
    iconColor3: String,
    iconColor4: String,
    iconColor5: String,
    iconTooltip: String, // Tooltip for icon btn 1
    iconTooltip2: String, // ^ for second
    iconTooltip3: String, // ^ for third
    iconTooltip4: String, // ^ for fourth
    itemsPerPage: Number,
    iconTooltip5: String, // ^ for fifth
    dependsOnProperty: String, //will only show first button if row property with this value is set to true
    dependsOnProperty2: String, //^ second button
    dependsOnProperty3: String, //^ third button
    dependsOnProperty4: String, //^ fourth button
    disableItemsPerPage: Boolean,
    checkboxOnProperty: String, //if set, will allow to show a checkbox depending on the given property
    checkboxOnProperty2: String, //^ second checkbox
    checkboxOnProperty3: String, //^ third checkbox,
    checkboxOnProperty4: String, //^ fourth checkbox,
    image: String, //name of property containing image path to display
    imageAlt: String, //alternative image if image couldn't be loaded
    linkName: String, // text for link which will be displayed
    linkName2: String, // ^ second link
    linkName3: String, // ^ third link
    linkRoute: String, // Route for link to direct to
    linkRoute2: String, // ^ second link
    linkRoute3: String, // ^ third link
    linkId: String, // ID for link for routing
    linkId2: String, // ^ second link
    linkId3: String, // ^ third link
    linkRouteMultipleIDs1: String, // first route part to direct to
    linkRouteMultipleIDs2: String, // second route part to direct to
    linkIdMultipleIDs1: String, // first ID for link for routing
    linkIdMultipleIDs2: String, // second ID for link for routing
    linkNameMultipleIDs: String, // text for link which will be displayed
    sortBy: String,
    sortDesc: Boolean,
    disableSearch: Boolean,
    trueFalseDecorateFields: Array,
    decorateFields: Array,
    expand: Boolean,
    expandFn: Function,
    filterName: String, //Name of filter object(e.g. advert, elevator)
    customPanel: Object,
    warningOrNormalText: String, // field of item which should be displayed as either a warning or normal text
    addTopScrollBar: Boolean,
    showSelect: Boolean,                // selection for deleting multiple rows
    selectionButton1Text: String,       // text for action-button for selected Items
    selectionButton1Color: String,      // color of the action-button for selected Items
  },
  data() {
    return {
      search: '',
      options: {
        sortBy: ["name"],
        sortDesc: [false]
      },
      loading: true,
      data: [],
      totalData: 0,
      searchTimer: null,
      tableUpdateTimer: null,
      selectedHeaders: null,
      selectedCheckBox: null,
      menu: false,
      isFilterOpen: false,
      filterKey: 0,
      tableWrapperWidth: null,
      tableWidth: null,
      renderTopScrollBar: true,
      selectedItems: []
    }
  },
  computed: {
    computedSearch: {
      get() {
        if (this.search) {
          this.$emit('cacheCommit', this.search);
          return this.search
        }
        this.$emit('cacheCommit', null);
        return null
      },
      set(value) {
        this.search = value;
      }
    },
    computedHeadersSet() {
      let result = [];

      if (typeof this.headerSet == 'undefined' || this.headerSet.length === 0) {
        return result;
      }

      for (let i = 0; i < this.headerSet.length; i++) {
        if (this.headerSet[i].hideInColumnMenu !== true
            && (typeof this.headerSet[i].hide === 'undefined'
              || this.headerSet[i].hide !== true)) {
          result.push(this.headerSet[i]);
        }
      }

      return result;
    }
  },
  created() {
    this.updateHeaders(this.headerSet);
  },
  mounted() {
    if (this.cachedSearch) {
      this.search = this.cachedSearch;
    }
    if (this.sortBy) {
      this.options.sortBy[0] = this.sortBy;
    }
    if (this.sortDesc != this.options.sortDesc[0]) {
      this.options.sortDesc[0] = this.sortDesc;
    }
    if(this.customPanel) {
      let element = this.customPanel;
      element.$mount();
      this.$refs.customPanel.appendChild(element.$el);
    }

    if (this.addTopScrollBar) {
      // add event listener when screen is being resized so we can resize top scroll bar
      window.addEventListener('resize', this.resizeTopBar);
    }
  },
  updated() {
    // have to do this in updated because in mounted, the false widths are retrieved from the table elements
    if (this.renderTopScrollBar && this.addTopScrollBar && this.data.length > 0) {
      let tableWrapper = document.getElementsByClassName('v-data-table__wrapper')[0];
      this.tableWrapperWidth = tableWrapper.offsetWidth;
      // really don't know why, but 52px have to be added additionally so the top scroll matches with the bottom one
      this.tableWidth = document.getElementsByTagName('table')[0].getBoundingClientRect().width + 52;

      // create new div for the scrollbar and set width and class
      let scrollbar = document.createElement('div');
      scrollbar.style.width = this.tableWidth + "px";
      scrollbar.className = 'scrollbar';

      // create new div for the scrollbar-wrapper and set width and class
      let scrollbarWrapper = document.createElement('div');
      scrollbarWrapper.className = 'scrollbar-wrapper';
      scrollbarWrapper.style.width = this.tableWrapperWidth + "px";
      // add the scrollbar inside the scrollbar-wrapper
      scrollbarWrapper.appendChild(scrollbar);

      // add the scrollbarWrapper with the scrollbar before the table element
      tableWrapper.before(scrollbarWrapper);

      // when top scroll bar is being scrolled, also scroll the bottom one
      scrollbarWrapper.addEventListener('scroll', () => {
        tableWrapper.scrollLeft = scrollbarWrapper.scrollLeft;
      })

      // when bottom scroll bar is being scrolled, also scroll the top one
      tableWrapper.addEventListener('scroll', () => {
        scrollbarWrapper.scrollLeft = tableWrapper.scrollLeft;
      })

      this.renderTopScrollBar = false;
    } else if (!this.renderTopScrollBar) {
      this.resizeTopBar();
    }
  },
  watch: {
    selectedItems() {
      this.$emit('changedItemSelection', this.selectedItems);
    },
    search() {
      this.clearSelection();
      this.computedSearch = this.search
      //use timer function to wait for user to finish until we search
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.options.page = 1; //reset the page back to 1 on search, otherwise we can land on an empty page
        this.callTableUpdate();
      }, 500);
    },
    selectedCheckBox() {
      this.selectedHeaders = [];
      this.$emit('updateHeaders', this.selectedCheckBox);
      for (let i = 0; i < this.headerSet.length; i++) {
        if ((typeof this.headerSet[i].hide === 'undefined'
            || this.headerSet[i].hide !== true)
          && ( this.headerSet[i].hideInColumnMenu === true
          || this.selectedCheckBox.indexOf(this.headerSet[i].value) >= 0)) {
          this.selectedHeaders.push(this.headerSet[i])
        }
      }
    },
    //by deep watching the table options, we can automatically call an update whenever sort/page changes
    options: {
      handler() {
        this.callTableUpdate();
      },
      deep: true,
      immediate: true //with this it will trigger on initialization -> we don't have to load on mount
    },
    cachedSearch() {
      if (this.cachedSearch) {
        this.search = this.cachedSearch;
      }
    },
    headers() {
      this.updateHeaders(this.headerSet);
    },
    headerSet() {
      this.updateHeaders(this.headerSet);
    }
  },
  methods: {
    /**
     * Method to send out for updating the table data.
     * Loading the correct data depends on the parent component, so we just pass an event to the parent.
     */
    callTableUpdate() {
      //using tiny timer to catch overlapping update calls made by the watch-functions
      let filter = this.$store.state.dtableFilter.model[this.filterName];
      clearTimeout(this.tableUpdateTimer);
      this.tableUpdateTimer = setTimeout(() => {
        this.$emit('updateTableData', this.options, this.search, filter);
      }, 50);
    },
    setFilterValue(key, value) {
      this.$store.state.dtableFilter.model[this.filterName][key] = value;
    },
    rerenderFilterPanel() {
      this.filterKey++
    },
    /**
     * Method for passing loaded data back into the table.
     *
     * @param data paginated data to pass into the table
     */
    fillTableData(data) {
      if (data == null) {
        this.data = [];
      } else {
        this.data = data.content;
      }
      this.loading = false;
      this.totalData = data && data.totalElements ? data.totalElements : 0;
    },
    clickedButton1(item) {
      this.$emit('clickedButton1', item);
    },
    clickedButton2(item) {
      this.$emit('clickedButton2', item);
    },
    clickedButton3(item) {
      this.$emit('clickedButton3', item);
    },
    clickedButton4(item) {
      this.$emit('clickedButton4', item);
    },
    clickedButton5(item) {
      this.$emit('clickedButton5', item);
    },
    clickedAddButton(item) {
      this.$emit('clickedAddButton', item);
    },
    clickedHeadButton(item) {
      this.$emit('clickedHeadButton', item);
    },
    checkShowButton(icon, property, item) {
      if (icon == null) {
        return false;
      }
      if (property == null) {
        return true;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    checkShowCheckbox(property, item) {
      if (property == null) {
        return false;
      }
      //check if the item in the given row has the required property and return it
      if (Object.prototype.hasOwnProperty.call(item, property)) {
        return item[property];
      }
      return false;
    },
    getHeader(key) {
      for (let i = 0; i < this.headerSet.length; i++) {
        if (this.headerSet[i].value == key) {
          return this.headerSet[i];
        }
      }

      return '';
    },
    updateHeaders(headerSet) {
      this.selectedHeaders = [];
      if (headerSet && headerSet.length > 0) {
        this.selectedHeaders = headerSet;
        this.selectedCheckBox = [];
        for (let i = 0; i < headerSet.length; i++) {
          if (headerSet[i].default === true && headerSet[i].hideInColumnMenu !== true) {
            this.selectedCheckBox.push(headerSet[i].value)
          }
        }
      } else {
        for (let i = 0; i < this.headers.length; i++) {
          if (this.headers[i].hide !== true) {
            this.selectedHeaders.push(this.headers[i]);
          }
        }
      }
    },
    returnLabel(item, field) {
      if (item[field] == true) {
        return 'done';
      }
      return 'block';
    },
    changeFilter(filter) {
      this.$emit('updateTableData', this.options, this.search, filter);
    },
    customPanelUpdated(fieldName, fieldValue) {
      this.$emit('customPanelValueUpdated', fieldName, fieldValue);
    },

    resizeTopBar() {
      // only run if tableWrapperWidth and tableWidth have been initialized before
      if (this.tableWrapperWidth && this.tableWidth) {
        this.tableWrapperWidth = document.getElementsByClassName('v-data-table__wrapper')[0].offsetWidth;
        this.tableWidth = document.getElementsByTagName('table')[0].getBoundingClientRect().width;

        // update the widths of the scrollbar and scrollbar-wrapper
        document.getElementsByClassName('scrollbar-wrapper')[0].style.width = this.tableWrapperWidth + "px";
        document.getElementsByClassName('scrollbar')[0].style.width = this.tableWidth + "px";
      }
    },

    clickedSelectionButton() {
      this.$emit("clickedSelectionButton1", this.selectedItems);
    },

    clearSelection() {
      if (this.showSelect) {
        this.selectedItems = [];
      }
    },

    toggleAll(toggleAllObject) {
      if (!toggleAllObject.value) {
        this.selectedItems =  [];
      } else {
        this.selectedItems = toggleAllObject.items;
      }
    }
  }
}

</script>
<style src="@/modules/core/components/DataTable/DataTable.scss" lang="scss"/>
